import { ReactNode, useEffect, useRef, useState, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { HelpCircle, ArrowUpRight, BookOpen } from "lucide-react";
import env from "../../config";

// A small "?" icon that reveals a popover explaining a single field. Drop it next
// to any label across the app: <HelpHint title="..." anchor="route-country">…</HelpHint>.
// `anchor` deep-links to the matching subsection of the standalone docs site
// (when REACT_APP_DOCS_URL is configured) via the "Learn more" button.
//
// Opens on hover (with a short grace delay so moving the cursor onto the popover
// doesn't dismiss it) and also on click/tap and keyboard focus. The trigger is a
// <span role="button"> (not a real <button>) so it can safely live inside other
// interactive elements — e.g. a collapsible section header button.
interface Props {
  title?: string;
  children: ReactNode;
  anchor?: string;
  className?: string;
}

const POP_W = 300;

const HelpHint = ({ title, children, anchor, className = "" }: Props) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const [place, setPlace] = useState<"top" | "bottom">("bottom");
  const [caretLeft, setCaretLeft] = useState(20);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const openT = useRef<number | null>(null);
  const closeT = useRef<number | null>(null);

  const position = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(
      Math.max(10, r.left + r.width / 2 - POP_W / 2),
      vw - POP_W - 10,
    );
    const below = vh - r.bottom;
    const above = below < 240 && r.top > below; // flip up when cramped below
    setPlace(above ? "top" : "bottom");
    setCaretLeft(Math.min(Math.max(16, r.left + r.width / 2 - left), POP_W - 16));
    setStyle(
      above
        ? { position: "fixed", bottom: vh - r.top + 10, left, width: POP_W }
        : { position: "fixed", top: r.bottom + 10, left, width: POP_W },
    );
  };

  const clearTimers = () => {
    if (openT.current) {
      clearTimeout(openT.current);
      openT.current = null;
    }
    if (closeT.current) {
      clearTimeout(closeT.current);
      closeT.current = null;
    }
  };

  const show = () => {
    clearTimers();
    position();
    setOpen(true);
  };
  // Hover-in: open after a tiny delay so brushing past doesn't flash it.
  const hoverOpen = () => {
    clearTimers();
    openT.current = window.setTimeout(show, 90);
  };
  // Hover-out: close after a grace period so the cursor can travel to the popover.
  const hoverClose = () => {
    clearTimers();
    closeT.current = window.setTimeout(() => setOpen(false), 220);
  };

  // Click/tap (and keyboard) toggle — immediate, and stops the event from
  // reaching whatever the trigger sits inside (e.g. a section header button).
  const toggle = (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation();
    e.preventDefault();
    clearTimers();
    if (open) setOpen(false);
    else show();
  };

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = () => setOpen(false); // position is fixed — close on scroll/resize
    document.addEventListener("mousedown", onDocDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  // Tidy up any pending timers on unmount.
  useEffect(() => clearTimers, []);

  const docHref = anchor && env.DOCS_URL ? `${env.DOCS_URL}#${anchor}` : null;

  return (
    <>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-label={title ? `Help: ${title}` : "What's this?"}
        onClick={toggle}
        onMouseEnter={hoverOpen}
        onMouseLeave={hoverClose}
        onFocus={show}
        onBlur={hoverClose}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle(e);
        }}
        className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full cursor-help transition-colors align-middle ${
          open ? "text-brand-600 bg-brand-50" : "text-ink-400 hover:text-brand-600 hover:bg-brand-50"
        } ${className}`}
      >
        <HelpCircle size={14} strokeWidth={2.25} />
      </span>
      {open &&
        style &&
        createPortal(
          <div
            ref={popRef}
            style={style}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={clearTimers}
            onMouseLeave={hoverClose}
            className="z-[70] rounded-xl border border-ink-200 bg-white anim-in"
          >
            {/* caret pointing at the trigger */}
            <span
              aria-hidden
              className={`absolute w-2.5 h-2.5 bg-white rotate-45 ${
                place === "bottom"
                  ? "-top-[5px] border-l border-t border-ink-200"
                  : "-bottom-[5px] border-r border-b border-ink-200"
              }`}
              style={{ left: caretLeft - 5 }}
            />
            <div
              className="relative rounded-xl p-3.5"
              style={{ boxShadow: "0 14px 44px -14px rgba(24,24,27,0.28)", background: "#fff" }}
            >
              {title && (
                <div className="flex items-center gap-1.5 mb-1.5">
                  <HelpCircle size={13} className="text-brand-500 shrink-0" />
                  <span className="text-[13px] font-semibold text-ink-900 tracking-tight">
                    {title}
                  </span>
                </div>
              )}
              <div className="text-[12.5px] text-ink-600 leading-relaxed">
                {children}
              </div>
              {docHref && (
                <a
                  href={docHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 h-7 px-2.5 rounded-lg bg-brand-50 text-brand-700 text-[12px] font-semibold hover:bg-brand-100 transition-colors"
                >
                  <BookOpen size={12} strokeWidth={2.25} />
                  Learn more
                  <ArrowUpRight size={12} strokeWidth={2.5} />
                </a>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default HelpHint;
